import React from 'react';
import SwipeToConfirm from '../SwipeToConfirm';
import TimeLibrary from '../../libraries/Time';

/**
 * The non-BernExpo parking card, laid out as the app lays it out.
 *
 * Shared by the owner page and the public share link. Those two rendered the
 * same card from two copies of the same JSX, which is how the app's newer
 * blocks ended up on only one of them; there is one copy now.
 *
 * The two pages read different payloads — /parking/me nests the lot under
 * `field`, the public DTO flattens it into field_* — so the caller passes plain
 * values rather than a ticket object.
 *
 * @component
 */
export default ({
  qrImage,
  qrPayload,
  usedAt,
  color,
  fieldName,
  entrance,
  mapsUrl,
  infoText,
  opponent,
  gameDate,
  location,
  canConfirm,
  confirming,
  onConfirm,
}) => (
  <div className="parking-ticket">
    {/* The QR stays on the card after it was confirmed — the status below says
        it is used, while the code itself remains readable at the barrier. Only
        the staff slider disappears, since there is nothing left to confirm.

        The lot's colour frames the QR, with the lot name on the frame: staff
        read the lot off a held-up screen, and the colour carries further than
        the name. Lots without a colour (BernExpo has its own design) render the
        QR unframed. */}
    {qrImage && (
      <div
        className={color ? 'parking-ticket-frame' : undefined}
        style={color ? { background: color } : undefined}
      >
        {!!color && <h2 className="parking-ticket-frame-title">{fieldName}</h2>}

        <div className="parking-ticket-qr">
          <img src={qrImage} alt="QR-Code" />

          {qrPayload && <span className="parking-ticket-qr-value">{qrPayload}</span>}
        </div>
      </div>
    )}

    {/* Under the code, not above it (client, 2026-09-17): the card opens with
        the thing you hold up at the barrier, and the state follows it. */}
    {!!usedAt && (
      <>
        {/* The app shows a green confirmation bar here, not a neutral notice —
            staff read this across a car window in the dark. */}
        <div className="parking-ticket-confirmed">
          <span className="parking-ticket-confirmed-check">✓</span>
          BESTÄTIGT
        </div>

        {/* Parking is single-use; say so rather than leaving it implied. */}
        <div className="parking-ticket-note">Keine Wiedereinfahrt</div>
      </>
    )}

    {/* Staff-only, and only while the lot is open (3h before puck-drop
        until 02:00). Kept as one dark block so it reads as an instruction
        to the person holding the phone, not as a button for the driver. */}
    {!usedAt && canConfirm && (
      <div className="parking-ticket-staff">
        <h3>NUR FÜR SCB-PERSONAL</h3>

        <SwipeToConfirm
          label="Nicht selbst wischen"
          loading={confirming}
          onConfirm={onConfirm}
        />

        <p>Die Bestätigung erfolgt ausschliesslich durch das SCB-Personal.</p>
      </div>
    )}

    {/* Two columns side by side, as in the app and in the season eticket. */}
    <div className="parking-ticket-boxes">
      <div className="parking-ticket-box">
        <h3>PARKPLATZ</h3>

        {/* With coordinates the lot name is itself the link. The icon and the
            accent colour carry the affordance; an underline under this weight
            just reads as heavy. */}
        {mapsUrl ? (
          <a className="parking-ticket-map" href={mapsUrl} target="_blank" rel="noopener noreferrer">
            <span role="img" aria-label="Karte">
              🗺
            </span>
            {fieldName}
          </a>
        ) : (
          <p className="parking-ticket-strong">{fieldName}</p>
        )}

        {!!entrance && <p className="parking-ticket-strong">Eingang: {entrance}</p>}
      </div>

      <div className="parking-ticket-box">
        <h3>SPIEL</h3>

        {!!opponent && <p className="parking-ticket-strong">SC Bern – {opponent}</p>}

        {!!gameDate && (
          <p>
            {TimeLibrary.date(gameDate)}
            <br />
            {TimeLibrary.time(gameDate)}
            {!!location && (
              <>
                <br />
                {location}
              </>
            )}
          </p>
        )}
      </div>
    </div>

    {!!infoText && (
      <div className="parking-ticket-box parking-ticket-box-wide">
        <h3>INFORMATION</h3>

        <p>{infoText}</p>
      </div>
    )}
  </div>
);
